import {
	CarriersEnum,
	EventTypeEnum,
	ProviderEnum,
	SCACEnum,
	ShipmentManifestStatusEnum,
	ShipmentRatingTypeEnum,
	TransactionTypeEnum,
} from '@app/app.enum';
import { listEndDate, listStartDate } from '@app/constants';
import * as models from '@app/models/index';
import { InjectedIntl } from 'react-intl';
import { IAxiosCancellation } from './core.models';
import { ILocationProps } from './core.models';

export interface IBalanceHistoryComponentProps extends IAxiosCancellation, models.ISnackBarFeature {
	intl: InjectedIntl;
	contained?: boolean;
	rows?: models.IAccountTransaction[];
	pageSize?: number;
	totalResults?: number;
	totalPages?: number;
	siblingCount?: number;
	updateCurrentPage?: (currentPage: number) => void;
	heading?: string;
	startDate?: Date;
	endDate?: Date;
	location?: ILocationProps;
	loading?: boolean;
}
export interface IAdminShipmentComponentProps extends IAxiosCancellation, models.ISnackBarFeature {}

export const initialAdminShipmentComponentState = (): IAdminShipmentComponentState => ({
	results: [],
	nextPage: null,
	previousPage: null,
	pageSize: 0,
	totalPages: 0,
	totalResults: 0,
	currentPage: 1,
	pageJump: 1,
	currentBalance: null,
	startDate: listStartDate.toDate(),
	endDate: listEndDate.toDate(),
	filterPanelExpanded: false,
	// Using Object.values for selecting all of the values in Enum object
	selectedCarrierSCACFilterValues: Object.values(SCACEnum),
	selectedCarrierCodeFilterValues: Object.values(CarriersEnum),
	selectedCarrierProviderFilterValues: Object.values(ProviderEnum),
	selectedShipmentManifestStatusFilterValues: Object.values(ShipmentManifestStatusEnum),
	selectedShipmentRatingFilterValues: Object.values(ShipmentRatingTypeEnum),
	selectedPartnerFilterValues: [],
	customerVisibleAccountNumber: '',
	selectAllCarrierSCACSelection: true,
	selectAllCarrierCodeSelection: true,
	selectAllCarrierProviderSelection: true,
	selectAllPartnerSelection: true,
	selectAllShipmentManifestStatusSelection: true,
	selectAllShipmentRatingSelection: true,
	disabledCheckBox: {},
	isOpenCarrierSCACFilter: false,
	isOpenCarrierCodeFilter: false,
	isOpenCarrierProviderFilter: false,
	isOpenPartnerFilter: false,
	isOpenShipmentManifestStatusFilter: false,
	isOpenShipmentRatingFilter: false,
	disableDatePickerFilter: false,
	dataLoading: false,
	trackingNumber: '',
	isFilterApplied: false,
});

export interface IAdminShipmentComponentState extends models.IPagedResult<models.IShipment> {
	currentPage: number;
	currentBalance: number;
	pageJump: number;
	startDate: Date;
	endDate: Date;
	filterPanelExpanded: boolean;
	selectedCarrierSCACFilterValues: string[];
	selectedCarrierCodeFilterValues: string[];
	selectedCarrierProviderFilterValues: string[];
	selectedPartnerFilterValues: string[];
	selectedShipmentManifestStatusFilterValues: string[];
	selectedShipmentRatingFilterValues: string[];
	customerVisibleAccountNumber: string;
	selectAllCarrierSCACSelection: boolean;
	selectAllCarrierCodeSelection: boolean;
	selectAllCarrierProviderSelection: boolean;
	selectAllShipmentManifestStatusSelection: boolean;
	selectAllShipmentRatingSelection: boolean;
	selectAllPartnerSelection: boolean;
	disabledCheckBox: object;
	isOpenCarrierSCACFilter: boolean;
	isOpenCarrierCodeFilter: boolean;
	isOpenCarrierProviderFilter: boolean;
	isOpenPartnerFilter: boolean;
	isOpenShipmentManifestStatusFilter: boolean;
	isOpenShipmentRatingFilter: boolean;
	disableDatePickerFilter: boolean;
	isFilterApplied: boolean;
	dataLoading: boolean;
	trackingNumber: string;
}

export interface IShipmentManifestComponentState extends models.IPagedResult<models.IShipmentManifestReportVM> {
	currentPage: number;
	currentBalance: number;
	pageJump: number;
	startDate: Date;
	endDate: Date;
	filterPanelExpanded: boolean;
	selectedManifestStatusFilterValues: string[];
	selectAllManifestStatusSelection: boolean;
	disabledCheckBox: object;
	isOpenManifestStatusFilter: boolean;
	disableDatePickerFilter: boolean;
	isFilterApplied: boolean;
	dataLoading: boolean;
	trackingNumber: string;
}

export interface IBalanceHistoryComponentState extends models.IPagedResult<models.IAccountTransaction> {
	currentPage: number;
	currentBalance: number;
	pageJump?: number;
	startDate: Date;
	endDate: Date;
	filterPanelExpanded: boolean;
	selectedEventTypeFilterValues: EventTypeEnum[];
	selectedTransactionTypeFilterValues: TransactionTypeEnum[];
	selectAllTransactionSelection: boolean;
	selectAllEventSelection: boolean;
	disabledCheckBox: object;
	isOpenTypeFilter: boolean;
	isOpenSubTypeFilter: boolean;
	disableDatePickerFilter: boolean;
	dataLoading: boolean;
}

export const initialReportBalanceHistoryComponentState = (
	results: models.IAccountTransaction[] = [],
	dataLoading = true
): IBalanceHistoryComponentState => ({
	results,
	nextPage: null,
	previousPage: null,
	pageSize: 0,
	totalPages: 0,
	totalResults: 0,
	currentPage: 1,
	currentBalance: null,
	pageJump: 1,
	startDate: listStartDate.toDate(),
	endDate: listEndDate.toDate(),
	filterPanelExpanded: false,
	// Using Object.values for selecting all of the values in Enum object
	selectedEventTypeFilterValues: Object.values(EventTypeEnum),
	selectedTransactionTypeFilterValues: Object.values(TransactionTypeEnum),
	selectAllTransactionSelection: true,
	selectAllEventSelection: true,
	disabledCheckBox: {},
	isOpenTypeFilter: false,
	isOpenSubTypeFilter: false,
	disableDatePickerFilter: false,
	dataLoading,
});

export interface IReportAccountStatementByDayComponentState extends models.IPagedResult<models.IAccountStatementByDay> {
	currentPage: number;
	pageJump: number;
	startDate: Date;
	endDate: Date;
	disableDatePickerFilter: boolean;
	dataLoading: boolean;
	cancelPreviousRequests: boolean;
}

export const initialReportAccountStatementByDayComponentState = (): IReportAccountStatementByDayComponentState => ({
	results: [],
	nextPage: null,
	previousPage: null,
	pageSize: 0,
	totalPages: 0,
	totalResults: 0,
	currentPage: 1,
	pageJump: 1,
	startDate: listStartDate.toDate(),
	endDate: listEndDate.toDate(),
	disableDatePickerFilter: false,
	dataLoading: true,
	cancelPreviousRequests: false,
});

export interface IAccountStatementByDay {
	date: Date;
	creditCardDebitTransactions: number;
	creditCardCreditTransactions: number;
	labelDebitTransactions: number;
	labelCreditTransactions: number;
	creditCardDebitAmount: number;
	creditCardCreditAmount: number;
	labelDebitAmount: number;
	labelCreditAmount: number;
	totalBalance: number;
	debitTransactions: number;
	creditTransactions: number;
	debitAmount: number;
	creditAmount: number;
	dailyBalance: number;
}

export interface IDailyFinanceComponentProps extends IAxiosCancellation, models.ISnackBarFeature {
	contained?: boolean;
	rows?: models.IDailyMonthlyFinance[];
	startDate?: Date;
	endDate?: Date;
}

export interface IReportDailyFinanceComponentState extends models.IPagedResult<models.IDailyMonthlyFinance> {
	currentPage: number;
	pageJump: number;
	startDate: Date;
	endDate: Date;
	disableDatePickerFilter: boolean;
	dataLoading: boolean;
}

export const initialReportDailyFinanceComponentState = (
	results: models.IDailyMonthlyFinance[] = [],
	dataLoading = true
): IReportDailyFinanceComponentState => ({
	results,
	nextPage: null,
	previousPage: null,
	pageSize: 0,
	totalPages: 0,
	totalResults: 0,
	currentPage: 1,
	pageJump: 1,
	startDate: listStartDate.toDate(),
	endDate: listEndDate.toDate(),
	disableDatePickerFilter: false,
	dataLoading,
});

export interface IDailyMonthlyFinance extends models.IAccountStatementByDay {
	beginningBalance: number;
	accountEntries?: models.IAccountTransaction[];
	dailyFinances?: models.IDailyMonthlyFinance[];
}

export interface IPreviousFilters {
	selectedEventTypeFilterValues?: EventTypeEnum[];
	selectedTransactionTypeFilterValues?: TransactionTypeEnum[];
	disabledCheckBox?: object;
	selectAllTransactionSelection?: boolean;
	selectAllEventSelection?: boolean;
}
export interface IAdminPreviousFilters {
	selectedCarrierSCACFilterValues?: string[];
	selectedCarrierCodeFilterValues?: string[];
	selectedCarrierProviderFilterValues?: string[];
	selectedPartnerFilterValues?: string[];
	selectedShipmentManifestStatusFilterValues?: string[];
	selectedShipmentRatingFilterValues?: string[];
	selectAllCarrierSCACSelection?: boolean;
	selectAllCarrierCodeSelection?: boolean;
	selectAllCarrierProviderSelection?: boolean;
	selectAllPartnerSelection?: boolean;
	selectAllShipmentManifestStatusSelection?: boolean;
	selectAllShipmentRatingSelection?: boolean;
	disabledCheckBox?: object;
}
export interface IAdminShipmentFilters {
	scac: string[];
	code: string[];
	provider: string[];
	partnerVisibleAccountNum: string[];
	shipmentManifestStatus: string[];
	shipmentRatingType: string[];
}

export const initialAdminShipmentFilter = (): IAdminShipmentFilters => ({
	scac: [],
	code: [],
	provider: [],
	partnerVisibleAccountNum: [],
	shipmentManifestStatus: [],
	shipmentRatingType: [],
});

export interface IAdminShipmentManifestPreviousFilters {
	selectedManifestStatusFilterValues?: string[];
	selectAllManifestStatusSelection?: boolean;
	disabledCheckBox?: object;
}

export interface IAdminShipmentManifestFilters {
	manifestStatus: ShipmentManifestStatusEnum[];
}


// WEBPACK FOOTER //
// ./src/models/report.models.ts